import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  NgZone,
  Output,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import {
  FileUploadControl,
  FileUploadValidators,
} from "@iplab/ngx-file-upload";
import { BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import {
  PatientDoc,
  UploadPatientDoc,
} from "src/app/core/models/patient-docs.model";
import { PatientDocsService } from "src/app/core/services/patient-docs.service";
import {
  PromptType,
  UploadDocumentType,
  Utils,
} from "src/app/core/services/utils";
import { DocumentPreviewComponent } from "../document-attachment/document-preview/document-preview.component";
import Swal from "sweetalert2";
import { NgForm } from "@angular/forms";
import {
  catchError,
  finalize,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from "rxjs";
import { extractInsurancePayerNameForSearch } from "src/app/core/utils/insurance-payer-advance-search.util";
import { LabType, OldLabInfo } from "src/app/core/models/old-labs.model";
import { cloneDeep } from "lodash";
import { OldLabsService } from "src/app/core/services/old-labs.service";
import { ApplicationCacheService } from "src/app/core/services/application-cache.service";
import { DatePipe } from "@angular/common";
import { AiService } from "src/app/core/services/ai.service";
import { InsuranceOcrResult } from "src/app/core/models/insurance-ocr.model";
import { PhotoIdOcrResult } from "src/app/core/models/photo-id-ocr.model";
import { Patient, UpsertPatient } from "src/app/core/models/patient.model";
import { PatientService } from "src/app/core/services/patient.service";
import { patientDocMetadataNonEmpty } from "src/app/core/helpers/patient-doc-metadata.util";

@Component({
  selector: "app-upload-document",
  templateUrl: "./upload-document.component.html",
  styleUrls: ["./upload-document.component.scss"],
})
export class UploadDocumentComponent implements OnDestroy {
  /** Expose enum in template (e.g. Photo ID OCR UI). */
  readonly UploadDocumentType = UploadDocumentType;
  @ViewChild("patientDocForm") patientDocForm: NgForm | undefined = undefined;
  @Input() documentType: UploadDocumentType;
  @Input() patientDoc: PatientDoc;
  @Input() heading: string = "Last Clinic Encounter";
  /** When true, the title row (heading / ordered) is hidden — use when the parent supplies the section label. */
  @Input() hideHeading = false;
  @Input() showOrderedCheckbox: boolean;
  @Input() isAiFile: boolean = false;
  /**
   * Compact empty state: plus icon + short label (e.g. secondary insurance slot in referral).
   * Does not affect other document types when false.
   */
  @Input() compactEmptyState = false;
  /**
   * Identification section: hide saved ID/insurance cards until OCR metadata exists.
   * Upload slot stays visible when nothing is saved; card stays visible while OCR runs.
   */
  @Input() displayOnlyWithMetadata = false;
  @Output() onDocumentUploaded = new EventEmitter<PatientDoc>();
  @Output() onDocumentRemoved = new EventEmitter<PatientDoc>();
  /** Emitted when ID / insurance OCR finishes successfully (referral-details can merge fields). */
  @Output() photoIdOcrCompleted = new EventEmitter<
    PhotoIdOcrResult | InsuranceOcrResult
  >();
  /** After OCR metadata is saved via PATCH; parent should refresh `patientDoc` from cache. */
  @Output() onDocumentMetadataSaved = new EventEmitter<void>();
  /** Emits when the file control gains or loses a local file (before/after upload). */
  @Output() hasLocalFileChange = new EventEmitter<boolean>();
  dosDate: Date;
  showOldEncounterWarning: boolean;
  isRemoving: boolean;
  newDoc: UploadPatientDoc;
  isSaving: boolean;
  isAnalyzing: boolean = false;
  /** ID / insurance card OCR in progress after upload. */
  isPhotoIdOcrRunning = false;
  /** Last OCR result (same API as Photo ID). */
  photoIdOcrResult: PhotoIdOcrResult | InsuranceOcrResult | null = null;
  private photoIdSourceFile: File | null = null;
  errorMsg: string = "";
  errorFound: boolean = false;

  recentCreatedDoc: PatientDoc;

  currentPatientName: string = "";
  currentPatientDOB: string = "";
  private subscription: Subscription;
  /** Raw blob URL (for revoke). Use {@link pendingUploadPreviewSafeUrl} in the template. */
  private pendingUploadPreviewUrl: string | null = null;
  /** Sanitized blob URL so Angular allows `img[src]` (unsafe `blob:` URLs are stripped otherwise). */
  pendingUploadPreviewSafeUrl: SafeUrl | null = null;
  labTypeToDocumentTypeMapping: Map<UploadDocumentType, LabType> = new Map([
    [UploadDocumentType.Labs, LabType.FullMetabolic],
    [UploadDocumentType.UltrasoundFindings, LabType.Ultrasound],
    [UploadDocumentType.NonInvasive, LabType.ABI],
  ]);
  labInfo: OldLabInfo;
  public readonly control = new FileUploadControl(
    {
      listVisible: true,
      discardInvalid: true,
      multiple: false,
    },
    [FileUploadValidators.filesLimit(1)]
  );

  constructor(
    private readonly _documentService: PatientDocsService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _oldLabService: OldLabsService,
    private readonly _modalService: BsModalService,
    private readonly _datePipe: DatePipe,
    private readonly _aiService: AiService,
    private readonly _patientService: PatientService,
    private readonly _sanitizer: DomSanitizer,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone
  ) {
    this.subscription = this.control.valueChanges.subscribe(
      (values: Array<File>) => {
        this._ngZone.run(() => {
          if (values?.length && values[0]) {
            this.setPendingUploadPreview(values[0]);
            this.getImage(values[0]);
          } else {
            this.revokePendingUploadPreview();
            if (this.showOcrStatus) {
              this.photoIdSourceFile = null;
              this.photoIdOcrResult = null;
            }
          }
          this.hasLocalFileChange.emit(!!(values?.length && values[0]));
          // Defer: valueChanges can fire during parent CD; sync detectChanges() throws
          // "Should be run in update mode" (Angular 15+).
          Promise.resolve().then(() => this._cdr.detectChanges());
        });
      }
    );
  }

  ngOnInit() {
    if (!this.showOrderedCheckbox) {
      return;
    }

    this.labInfo =
      this.labInfoCache ||
      new OldLabInfo(
        this.selectedEncounterId,
        this.labTypeToDocumentTypeMapping.get(this.documentType)
      );
  }

  public ngOnDestroy(): void {
    this.revokePendingUploadPreview();
    this.subscription.unsubscribe();
  }

  private revokePendingUploadPreview(): void {
    if (this.pendingUploadPreviewUrl) {
      URL.revokeObjectURL(this.pendingUploadPreviewUrl);
      this.pendingUploadPreviewUrl = null;
    }
    this.pendingUploadPreviewSafeUrl = null;
  }

  /** Builds a blob URL for image files only; PDF and other types use the static doc icon in the template. */
  private setPendingUploadPreview(file: File): void {
    this.revokePendingUploadPreview();
    if (this.isImageFileForPreview(file)) {
      const url = URL.createObjectURL(file);
      this.pendingUploadPreviewUrl = url;
      this.pendingUploadPreviewSafeUrl =
        this._sanitizer.bypassSecurityTrustUrl(url);
    }
  }

  private isImageFileForPreview(file: File): boolean {
    const t = file?.type ?? "";
    if (t.startsWith("image/")) {
      return true;
    }
    return /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(file?.name ?? "");
  }

  /**
   * Saved document row: show image thumbnail from `fileLink` when the doc is an image;
   * otherwise the template shows the PDF icon.
   */
  get savedDocThumbnailSafeUrl(): SafeUrl | null {
    const doc = this.patientDoc;
    if (!doc?.fileLink?.trim() || !this.isPatientDocImageFile(doc)) {
      return null;
    }
    return this._sanitizer.bypassSecurityTrustResourceUrl(doc.fileLink.trim());
  }

  private isPatientDocImageFile(doc: PatientDoc): boolean {
    const extRaw = (doc.extension || "").replace(/^\./, "").toLowerCase();
    if (extRaw === "pdf") {
      return false;
    }
    const name = (doc.fileName || doc.name || "").toLowerCase();
    if (/\.pdf(\?|$|#)/i.test(name) || name.endsWith(".pdf")) {
      return false;
    }
    const linkPath = (doc.fileLink || "").split(/[?#]/)[0].toLowerCase();
    if (linkPath.endsWith(".pdf")) {
      return false;
    }

    const imageExtSet = new Set([
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "bmp",
      "heic",
      "heif",
      "tif",
      "tiff",
    ]);
    if (extRaw && imageExtSet.has(extRaw)) {
      return true;
    }
    return (
      /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(name) ||
      /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(linkPath)
    );
  }

  /** Photo ID, primary insurance, and secondary insurance use the same ID-card OCR reader. */
  get showOcrStatus(): boolean {
    return (
      this.documentType === UploadDocumentType.Identification ||
      this.documentType === UploadDocumentType.InsuranceInfo ||
      this.documentType === UploadDocumentType.SecondaryInsurance
    );
  }

  /** Blue OCR icon when we have session OCR result or persisted `patientDoc.metadata`. */
  get showOcrBlueIcon(): boolean {
    if (!this.showOcrStatus) {
      return false;
    }
    if (this.photoIdOcrResult) {
      return true;
    }
    return patientDocMetadataNonEmpty(this.patientDoc?.metadata);
  }

  /**
   * When `displayOnlyWithMetadata` is set (referral IDENTIFICATION slots):
   * show upload when empty; always show a saved file, with or without OCR metadata.
   */
  get showDocumentSlot(): boolean {
    if (!this.displayOnlyWithMetadata || !this.showOcrStatus) {
      return true;
    }
    const hasSavedFile = !!this.patientDoc?.fileLink?.trim();
    if (!hasSavedFile || this.errorFound) {
      return true;
    }
    // Saved document: show whether or not OCR metadata exists.
    return true;
  }

  /** Photo ID OCR block: show when we have a name and/or DOB from OCR/metadata. */
  get photoIdOcrSummaryVisible(): boolean {
    if (this.documentType !== UploadDocumentType.Identification) {
      return false;
    }
    return !!(
      this.photoIdMetadataName.trim() || this.photoIdMetadataDobDisplay.trim()
    );
  }

  /** Display name: title case (handles hyphenated parts, e.g. Eldine-Logan). */
  get photoIdDisplayNameFormatted(): string {
    if (this.documentType !== UploadDocumentType.Identification) {
      return "";
    }
    return this.toTitleCaseName(this.photoIdMetadataName);
  }

  /** Insurance boxes: show two-line block (name + payer ID) when we have OCR/metadata or fallback label. */
  get insuranceTwoLineBlockVisible(): boolean {
    if (!this.isInsuranceDocumentType()) {
      return false;
    }
    return !!(
      this.insuranceNameLineDisplay.trim() ||
      this.resolveInsurancePayerCode()
    );
  }

  /** Line 1: title-cased payer/plan name from OCR, or document label fallback. */
  get insuranceNameLineDisplay(): string {
    if (!this.isInsuranceDocumentType()) {
      return "";
    }
    const meta = this.patientDoc?.metadata as
      | Record<string, unknown>
      | undefined;
    const session = this.photoIdOcrResult as InsuranceOcrResult | null;
    const raw = extractInsurancePayerNameForSearch(meta, session)?.trim();
    if (raw) {
      return this.toTitleCaseName(raw);
    }
    return (this.patientDoc?.name || "").trim();
  }

  /** Payer id/code for line 2 (`Payer ID: …`). */
  get insurancePayerCodeLine(): string {
    return this.resolveInsurancePayerCode();
  }

  private resolveInsurancePayerCode(): string {
    if (!this.isInsuranceDocumentType()) {
      return "";
    }
    const meta = this.patientDoc?.metadata as
      | Record<string, unknown>
      | undefined;
    const session = this.photoIdOcrResult as InsuranceOcrResult | null;
    const payerCodeKeys = ["PayerCode", "payerCode", "Code"];
    const codeFromSession = session
      ? this.firstStringFromMeta(
          session as Record<string, unknown>,
          payerCodeKeys
        )
      : "";
    const codeFromMetadata = meta
      ? this.firstStringFromMeta(meta, payerCodeKeys)
      : "";
    return (codeFromSession || codeFromMetadata || "").trim();
  }

  get documentUpdatedByDisplay(): string {
    const v = this.patientDoc?.updatedBy?.trim();
    return v || "Unknown";
  }

  /** Title case per word; hyphenated segments get each segment capitalized. */
  private toTitleCaseName(value: string): string {
    const t = (value ?? "").trim();
    if (!t) {
      return "";
    }
    return t
      .toLowerCase()
      .split(/\s+/)
      .map((word) =>
        word
          .split("-")
          .map((part) =>
            part ? part.charAt(0).toUpperCase() + part.slice(1) : ""
          )
          .join("-")
      )
      .join(" ");
  }

  get photoIdMetadataName(): string {
    if (this.documentType !== UploadDocumentType.Identification) {
      return "";
    }
    const session = this.photoIdOcrResult as PhotoIdOcrResult | null;
    if (session?.PatientName?.trim()) {
      return session.PatientName.trim();
    }
    const meta = this.patientDoc?.metadata as
      | Record<string, unknown>
      | undefined;
    return meta
      ? this.firstStringFromMeta(meta, ["PatientName", "patientName"])
      : "";
  }

  get photoIdMetadataDobDisplay(): string {
    if (this.documentType !== UploadDocumentType.Identification) {
      return "";
    }
    const session = this.photoIdOcrResult as PhotoIdOcrResult | null;
    const fromSession = session?.DOB?.trim();
    const meta = this.patientDoc?.metadata as
      | Record<string, unknown>
      | undefined;
    const fromMeta = meta
      ? this.firstStringFromMeta(meta, ["DOB", "dob"])
      : "";
    const raw = fromSession || fromMeta;
    if (!raw) {
      return "";
    }
    return this.normalizePhotoIdDobDisplay(raw);
  }

  private firstStringFromMeta(
    meta: Record<string, unknown>,
    keys: string[]
  ): string {
    for (const key of keys) {
      const v = meta[key];
      if (v != null && String(v).trim() !== "") {
        return String(v).trim();
      }
    }
    return "";
  }

  private normalizePhotoIdDobDisplay(value: string): string {
    const t = value.trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(t)) {
      return t;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(t)) {
      const [mo, d, y] = t.split("-");
      return `${mo}/${d}/${y}`;
    }
    return t;
  }

  /**
   * Photo ID OCR name + DOB align with encounter patient chart (cache).
   */
  get photoIdMatchesEncounterDemographics(): boolean {
    if (this.documentType !== UploadDocumentType.Identification) {
      return false;
    }
    const ocrName = this.photoIdMetadataName.trim();
    const ocrDob = this.photoIdMetadataDobDisplay.trim();
    if (!ocrName || !ocrDob) {
      return false;
    }
    const patient = this._applicationCacheService.applicationCache.patient;
    if (!patient?.firstName?.trim() || !patient?.lastName?.trim()) {
      return false;
    }
    if (!this.photoIdNameMatchesPatient(ocrName, patient)) {
      return false;
    }
    const chartDob = this.patientChartDobMmDdYyyy(patient);
    return chartDob !== "" && ocrDob === chartDob;
  }

  /**
   * Both name + DOB present on Photo ID OCR but they do not match patient demographics.
   */
  get photoIdDemographicsMismatchWarning(): boolean {
    if (this.documentType !== UploadDocumentType.Identification) {
      return false;
    }
    const ocrName = this.photoIdMetadataName.trim();
    const ocrDob = this.photoIdMetadataDobDisplay.trim();
    if (!ocrName || !ocrDob) {
      return false;
    }
    return !this.photoIdMatchesEncounterDemographics;
  }

  openPhotoIdDemographicsUpdateModal(evt?: Event): void {
    evt?.stopPropagation();
    if (this.documentType !== UploadDocumentType.Identification) {
      return;
    }
    const current = this._applicationCacheService.applicationCache.patient;
    if (!current?.id) {
return;
    }
    const ocrName = this.photoIdMetadataName.trim();
    const ocrDob = this.photoIdMetadataDobDisplay.trim();
    const chartFullName = [
      current.firstName,
      current.middleName,
      current.lastName,
    ]
      .filter((p) => p && String(p).trim())
      .map((p) => String(p).trim())
      .join(" ");
    const chartDobDisplay = this.patientChartDobMmDdYyyy(current) || "";
    const identifiedNameDisplay = this.escapeHtml(ocrName || "—");
    const identifiedDobDisplay = this.escapeHtml(
      ocrDob ? this.normalizePhotoIdDobDisplay(ocrDob) : "—"
    );

    Swal.fire({
      title: "Update patient demographics",
      html: `
        <div class="photo-id-demographics-form photo-id-demographics-form--two-col">
          <div class="photo-id-demographics-form__section photo-id-demographics-form__section--current">
            <div class="photo-id-demographics-form__section-heading">Current Demographics</div>
            <div class="photo-id-demographics-form__field">
              <label for="swal-photo-id-current-name" class="photo-id-demographics-form__label">Full legal name</label>
              <input
                id="swal-photo-id-current-name"
                type="text"
                class="photo-id-demographics-form__input"
                autocomplete="name"
                placeholder="First Middle Last"
                value="${this.escapeHtml(chartFullName)}"
              />
            </div>
            <div class="photo-id-demographics-form__field photo-id-demographics-form__field--last">
              <label for="swal-photo-id-current-dob" class="photo-id-demographics-form__label">Date of birth</label>
              <input
                id="swal-photo-id-current-dob"
                type="text"
                class="photo-id-demographics-form__input"
                inputmode="numeric"
                autocomplete="bday"
                placeholder="MM/DD/YYYY"
                value="${this.escapeHtml(chartDobDisplay)}"
              />
              <span class="photo-id-demographics-form__hint">Use format MM/DD/YYYY</span>
            </div>
          </div>
          <div class="photo-id-demographics-form__section photo-id-demographics-form__section--identified">
            <div class="photo-id-demographics-form__section-heading">Identified Demographics</div>
            <div class="photo-id-demographics-form__field">
              <span class="photo-id-demographics-form__label">Full legal name</span>
              <div class="photo-id-demographics-form__readonly">${identifiedNameDisplay}</div>
            </div>
            <div class="photo-id-demographics-form__field photo-id-demographics-form__field--last">
              <span class="photo-id-demographics-form__label">Date of birth</span>
              <div class="photo-id-demographics-form__readonly">${identifiedDobDisplay}</div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Save changes",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      focusConfirm: false,
      customClass: {
        popup: "photo-id-demographics-popup",
        confirmButton: "photo-id-demographics-popup__btn photo-id-demographics-popup__btn--primary",
        cancelButton: "photo-id-demographics-popup__btn photo-id-demographics-popup__btn--muted",
        actions: "photo-id-demographics-popup__actions",
      },
      didOpen: () => {
        const nameInput = document.getElementById(
          "swal-photo-id-current-name"
        ) as HTMLInputElement | null;
        nameInput?.focus();
        nameInput?.select();
      },
      preConfirm: () => {
        const nameEl = document.getElementById(
          "swal-photo-id-current-name"
        ) as HTMLInputElement | null;
        const dobEl = document.getElementById(
          "swal-photo-id-current-dob"
        ) as HTMLInputElement | null;
        const fullName = (nameEl?.value || "").trim().replace(/\s+/g, " ");
        const dobText = (dobEl?.value || "").trim();
        if (!fullName) {
          Swal.showValidationMessage("Name is required.");
          return null;
        }
        const parts = fullName.split(" ");
        if (parts.length < 2) {
          Swal.showValidationMessage("Please enter first and last name.");
          return null;
        }
        const isoDob = this.mmDdYyyyToIsoDate(dobText);
        if (!isoDob) {
          Swal.showValidationMessage("DOB must be in MM/DD/YYYY format.");
          return null;
        }
        return { parts, isoDob };
      },
    }).then((res) => {
      if (!res.isConfirmed || !res.value) {
        return;
      }
      const { parts, isoDob } = res.value as {
        parts: string[];
        isoDob: string;
      };
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");
      const payload = new UpsertPatient(current);
      payload.firstName = firstName;
      payload.lastName = lastName;
      payload.middleName = "";
      payload.dob = isoDob;
      this._patientService.updatPatient(current.id, payload).pipe(take(1)).subscribe({
        next: (updated) => {
          this._applicationCacheService.updatePatientInfoCache(updated);
},
        error: (err) => {
},
      });
    });
  }

  private isInsuranceDocumentType(): boolean {
    return (
      this.documentType === UploadDocumentType.InsuranceInfo ||
      this.documentType === UploadDocumentType.SecondaryInsurance
    );
  }

  private patientChartDobMmDdYyyy(patient: Patient): string {
    const raw = patient.dob?.split("T")?.[0]?.trim();
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return "";
    }
    const [y, m, d] = raw.split("-");
    return `${m}/${d}/${y}`;
  }

  private photoIdNameMatchesPatient(ocrName: string, patient: Patient): boolean {
    const metaNorm = this.normalizeComparableName(ocrName);
    const first = this.normalizeComparableName(patient.firstName);
    const last = this.normalizeComparableName(patient.lastName);
    const middle = patient.middleName?.trim()
      ? this.normalizeComparableName(patient.middleName)
      : "";
    const variants: string[] = [
      `${first} ${last}`,
      middle ? `${first} ${middle} ${last}` : "",
      `${last} ${first}`,
      `${last}, ${first}`,
      middle ? `${last} ${middle} ${first}` : "",
    ];
    const set = new Set(
      variants
        .map((v) => this.normalizeComparableName(v))
        .filter((v) => v.length > 0)
    );
    return [...set].some((v) => v === metaNorm);
  }

  private normalizeComparableName(s: string): string {
    return s
      .toLowerCase()
      .replace(/,/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private mmDdYyyyToIsoDate(value: string): string | null {
    const t = value.trim();
    const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) {
      return null;
    }
    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    const dt = new Date(year, month - 1, day);
    if (
      dt.getFullYear() !== year ||
      dt.getMonth() !== month - 1 ||
      dt.getDate() !== day
    ) {
      return null;
    }
    return `${String(year).padStart(4, "0")}-${String(month).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
  }

  private escapeHtml(value: string): string {
    return (value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;");
  }

  private getImage(file: File): void {
    if (this.showOcrStatus) {
      this.photoIdSourceFile = file;
      this.photoIdOcrResult = null;
    }
    let that = this;
    if (this.isAiFile) {
      this.isAnalyzing = true;
      if (FileReader && file) {
        const fr = new FileReader();
        fr.onload = (e) => {
          that.newDoc = new UploadPatientDoc(
            that.selectedPatientId,
            that.selectedEncounterId,
            that.selectedProcedureId,
            that.documentType
          );
          this.newDoc.name = Utils.splitCamelCase(this.documentType);
          const base64Splitted = (e.target as any).result.split("base64,");
          that.newDoc.docData.base64String = base64Splitted[1];
          that.newDoc.docData.contentType = base64Splitted[0]
            .replace(";", "")
            .replace("data:", "");

          that.uploadAndAnalyzeFile();
        };
        fr.readAsDataURL(file);
      }
    } else {
      if (FileReader && file) {
        const fr = new FileReader();
        fr.onload = (e) => {
          this.newDoc = new UploadPatientDoc(
            this.selectedPatientId,
            this.selectedEncounterId,
            this.selectedProcedureId,
            this.documentType
          );
          this.newDoc.name = Utils.splitCamelCase(this.documentType);
          const base64Splitted = (e.target as any).result.split("base64,");
          this.newDoc.docData.base64String = base64Splitted[1];
          this.newDoc.docData.contentType = base64Splitted[0]
            .replace(";", "")
            .replace("data:", "");
        };
        fr.readAsDataURL(file);
      }
    }
  }

  uploadAndAnalyzeFile() {
    if (
      !this.newDoc ||
      !this.newDoc.docData?.base64String ||
      !this.newDoc.docData?.contentType
    ) {
      return;
    }

    const payLoad = { ...this.newDoc };

    this._documentService
      .savePatientDoc(payLoad)
      .subscribe({
        next: (newlyCreatedDoc: PatientDoc) => {
          this._applicationCacheService.addDocumentToCache(newlyCreatedDoc);
          this.recentCreatedDoc = newlyCreatedDoc;

          this._documentService
            .readFileAndFetchDetailsJson(
              newlyCreatedDoc?.fileKey,
              PromptType.ValidateFile
            )
            .subscribe({
              next: (response: any) => {
                if (!response) {
                  return;
                }
                this.isAnalyzing = false;
                this.parseDosDate(response.DOS);
                const patientInfo =
                  this._applicationCacheService.applicationCache.patient;
                this.currentPatientName = `${patientInfo.firstName} ${patientInfo.lastName}`;
                this.currentPatientDOB = patientInfo.dob;

                if (
                  response.PatientName.toLowerCase() !=
                  this.currentPatientName.toLowerCase()
                ) {
                  this.errorFound = true;
                  this.errorMsg = "Patient Mismatch";
                  return;
                }

                const formattedDate = this._datePipe.transform(
                  this.currentPatientDOB,
                  "MM/dd/yyyy"
                );
                const responseDOB = this._datePipe.transform(
                  response.DOB,
                  "MM/dd/yyyy"
                );

                if (responseDOB != formattedDate) {
                  this.errorFound = true;
                  this.errorMsg = "DOB Mismatch";
                  return;
                }
              },
              error: (error) => {
                this.isAnalyzing = false;
              },
            });
        },
        error: (err) => {
},
      })
      .add(() => (this.isAnalyzing = true));
  }

  importFile() {
    this.onDocumentUploaded.emit(this.recentCreatedDoc);
  }

  uploadFile() {
    // Set date to today by default when uploading
    const today = new Date();
    if (!this.dosDate) {
      this.dosDate = today;
    }
    // Set dos to today's date if not already set
    if (!this.newDoc?.dos) {
      if (!this.newDoc) {
        return;
      }
      this.newDoc.dos = today.toISOString();
    }
    if (!this.patientDocForm) {
      return;
    }
    this.patientDocForm.form.markAllAsTouched();
    // Skip validation for dos field since it's hidden and set programmatically
    const dosControl = this.patientDocForm.form.get('dos');
    if (dosControl?.invalid) {
      dosControl.setErrors(null);
      dosControl.markAsTouched();
    }
    if (this.patientDocForm?.invalid) {
      return;
    }
    if (
      !this.newDoc ||
      !this.newDoc.docData?.base64String ||
      !this.newDoc.docData?.contentType
    ) {
      return;
    }
    this.isSaving = true;
    const payLoad = { ...this.newDoc };

    this._documentService
      .savePatientDoc(payLoad)
      .subscribe({
        next: (newlyCreatedDoc: PatientDoc) => {
          this._applicationCacheService.addDocumentToCache(newlyCreatedDoc);
          this.onDocumentUploaded.emit(newlyCreatedDoc);
          this.maybeRunPhotoIdOcrAfterUpload(newlyCreatedDoc?.id);
        },
        error: (err) => {
},
      })
      .add(() => (this.isSaving = false));
  }

  /**
   * After document is saved, run ID-card OCR on the same file (multipart) when applicable,
   * then PATCH document metadata with the parsed JSON.
   */
  private maybeRunPhotoIdOcrAfterUpload(documentId: string | undefined): void {
    if (!this.showOcrStatus) {
      return;
    }
    const file = this.photoIdSourceFile;
    if (!file) {
      return;
    }
    this.isPhotoIdOcrRunning = true;
    const ocr$: Observable<PhotoIdOcrResult | InsuranceOcrResult> =
      this.documentType === UploadDocumentType.Identification
        ? this._aiService.readPhotoIdOcr(file)
        : this._aiService.readInsuranceDocOcr(file);
    ocr$
      .pipe(
        take(1),
        tap((data) => {
          console.log("Document OCR (decoded JSON):", this.documentType, data);
          this.photoIdOcrResult = data;
          this.photoIdOcrCompleted.emit(data);
        }),
        switchMap((data) => {
          const id = documentId?.trim();
          if (!id) {
            return of(null);
          }
          const meta = this.ocrResultToMetadata(data);
          return this._documentService.patchDocumentMetadata(id, meta).pipe(
            tap(() => {
              this._applicationCacheService.mergeDocumentMetadataInCache(id, meta);
              this.onDocumentMetadataSaved.emit();
            }),
            catchError((err) => {
              console.error("OCR metadata PATCH failed:", err);
              return of(null);
            })
          );
        }),
        finalize(() => {
          this.isPhotoIdOcrRunning = false;
        })
      )
      .subscribe();
  }

  /** Plain JSON-serializable object for API `metadata` payload. */
  private ocrResultToMetadata(
    data: PhotoIdOcrResult | InsuranceOcrResult
  ): Record<string, unknown> {
    return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
  }

  parseDosDate(date: string) {
    if (!date) {
      this.dosDate = null;
      this.newDoc.dos = null;
      return;
    }
    this.dosDate = new Date(date);
    this.newDoc.dos = new Date(date).toISOString();
  }

  openDocumentViewModal() {
    const options: ModalOptions = {
      initialState: {
        signedLink: this.patientDoc.fileLink,
        documentName: this.patientDoc.name,
        documentExtension: this.patientDoc.extension,
        fileKey: this.patientDoc.fileKey || this.patientDoc.key,
      },
      class: "modal-xl child-modal modal-dialog-scrollable",
      ignoreBackdropClick: true,
    };
    this._modalService.show(DocumentPreviewComponent, options);
  }

  removePatientDoc() {
    Swal.fire({
      title: "Delete Patient Document?",
      text: "Patient document will be removed, Proceed?.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#513DCA",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Delete",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.isRemoving = true;
      this._documentService
        .deletePatientDoc(this.patientDoc.id)
        .subscribe({
          next: () => {
            this._applicationCacheService.removePatientDocFromCache(
              this.patientDoc.id
            );
            this.onDocumentRemoved.emit(this.patientDoc);
          },
          error: (err) => {
},
        })
        .add(() => (this.isRemoving = false));
    });
  }

  onOrderPlacedChange() {
    if (!this.labInfo.isOrderPlaced) {
      this.labInfo.whenDate = "";
      this.labInfo.where = null;
    }
    if (!this.hasChanges) {
      return;
    }
    Swal.fire({
      title: "Save Lab Info?",
      text: "Do you want to save these changes?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#513DCA",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Save",
    }).then((result) => {
      if (!result.isConfirmed) {
        this.labInfo.isOrderPlaced = this.labInfoCache?.isOrderPlaced;
        this.labInfo.whenDate = this.labInfoCache?.whenDate;
        this.labInfo.where = this.labInfoCache?.where;
        return;
      }
      this.save();
    });
  }

  save() {
    this._oldLabService
      .saveLabResults(this.labInfo)
      .subscribe({
        next: (response: OldLabInfo) => {
          this.labInfo = response;
          this._applicationCacheService.updateLabInfoCache(response);
        },
        error: (err) => {
},
      })
      .add(() => (this.isSaving = false));
  }

  get selectedPatientId(): string {
    return this._applicationCacheService.applicationCache.patient.id;
  }

  get selectedProcedureId(): string {
    return this._applicationCacheService.applicationCache.appointment
      .encounterProcedure.id;
  }
  get selectedEncounterId(): string {
    return this._applicationCacheService.applicationCache.appointment.id;
  }

  get hasChanges(): boolean {
    return (
      (this.labInfo.whenDate || "") !== (this.labInfoCache?.whenDate || "") ||
      (this.labInfo.where || null) !== (this.labInfoCache?.where || null)
    );
  }
  get labInfoCache() {
    return cloneDeep(
      this._applicationCacheService.applicationCache.labs.find(
        (x) =>
          x.type === this.labTypeToDocumentTypeMapping.get(this.documentType)
      )
    );
  }
}
